import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import AddDictionaryDrawer from './AddDictionaryDrawer';
import LookupSidebar from './LookupSidebar';
import LookupCanvasTable from './LookupCanvasTable';
import { useLookupDictionaries } from './hooks/useLookupDictionaries';
import { PageRoot } from './style';
import type { LookupDictionary } from './types';

interface Props {
  periodId?: string;
  reportType?: string;
  canManageWorkflow?: boolean;
}

const LookupTables: React.FC<Props> = ({ periodId, reportType, canManageWorkflow = true }) => {
  const {
    general,
    custom,
    all,
    isLoading,
    saveDictionary,
    createDictionary,
    deleteDictionary,
    uploadCsv,
    isMutating,
    isDeleting
  } = useLookupDictionaries({ periodId, reportType });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<LookupDictionary | null>(null);
  // `draft` refreshed via render, but `handleSave` reads through a ref so
  // it always picks up the latest value even when React is still batching
  // a setDraft from a blur-triggered `endedit` that fired synchronously
  // when the Save button received focus.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Edit-counter replaces the per-keystroke JSON.stringify(draft) !==
  // JSON.stringify(source) dirty check, which was O(rows * cols) on every
  // keystroke and dominated the 50k-row freeze. Bumps once per mutation.
  const [editVersion, setEditVersion] = useState(0);

  // Default-select the first dictionary so the right pane isn't empty.
  useEffect(() => {
    if (!selectedId && all.length > 0) {
      setSelectedId(all[0].id);
    }
  }, [selectedId, all]);

  // If the selected dictionary disappears (e.g. after delete), pick another.
  useEffect(() => {
    if (selectedId && !all.find(d => d.id === selectedId)) {
      setSelectedId(all[0]?.id ?? null);
    }
  }, [selectedId, all]);

  const source = useMemo(() => all.find(d => d.id === selectedId) ?? null, [all, selectedId]);

  // Reset draft from the persisted source whenever selection changes.
  useEffect(() => {
    setDraft(source);
    setEditVersion(0);
  }, [source]);

  const dirty = !!draft && !draft.readonly && editVersion > 0;

  const handleChange = useCallback((next: LookupDictionary) => {
    if (next.readonly) return;
    setDraft(next);
    setEditVersion(v => v + 1);
  }, []);

  const handleSave = async () => {
    // Yield one macrotask so any batched setDraft from a blur-triggered
    // `endedit` (which fires synchronously when the Save button steals
    // focus from an active cell editor) has time to flush into a fresh
    // render. Without this, `draftRef.current` still reads the pre-edit
    // dictionary when the user typed a value and hit Save without first
    // pressing Enter or tabbing away.
    await new Promise(resolve => setTimeout(resolve, 0));
    const d = draftRef.current;
    if (!d || d.readonly) return;
    try {
      await saveDictionary(d);
      setEditVersion(0);
      toast.success(`${d.name} saved`);
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Could not save changes');
    }
  };

  const handleCreate = async (dict: LookupDictionary) => {
    try {
      await createDictionary(dict);
      toast.success(`${dict.name} created`);
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Could not create dictionary');
    }
  };

  const handleUpload = async (args: { file: File; name?: string }) => {
    try {
      await uploadCsv(args);
      toast.success('CSV uploaded');
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Could not upload CSV');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDictionary(id);
      toast.success('Dictionary deleted');
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Could not delete dictionary');
    }
  };

  if (!periodId) {
    return (
      <PageRoot id="lookup-tables-root-empty-period">
        <div
          id="lookup-tables-select-period"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#868e96',
            fontSize: 14
          }}
        >
          Select a period to manage lookup tables.
        </div>
      </PageRoot>
    );
  }

  return (
    <PageRoot id="lookup-tables-root">
      <LookupSidebar
        idPrefix="lookup-tables-sidebar"
        general={general}
        custom={custom}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onAdd={() => setDrawerOpen(true)}
        onDelete={handleDelete}
        isLoading={isLoading}
        isDeleting={isDeleting}
      />
      {draft ? (
        <LookupCanvasTable
          idPrefix="lookup-tables-canvas"
          dictionary={draft}
          onChange={handleChange}
          onSave={handleSave}
          dirty={dirty}
          isSaving={isMutating}
          canManageWorkflow={canManageWorkflow}
        />
      ) : (
        <div
          id="lookup-tables-empty-state"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#868e96',
            fontSize: 14
          }}
        >
          {isLoading ? 'Loading…' : 'Select a dictionary to begin.'}
        </div>
      )}
      <AddDictionaryDrawer
        idPrefix="lookup-tables-drawer"
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreate={handleCreate}
        onUploadCsv={handleUpload}
        isSubmitting={isMutating}
      />
    </PageRoot>
  );
};

export default LookupTables;
